import React, { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/fileshare/components/ui/sonner";
import { AuthProvider } from "@/fileshare/context/AuthContext";
import Navbar from "@/fileshare/components/Navbar";
import InstallPrompt from "@/fileshare/components/InstallPrompt";
import ProtectedRoute from "@/fileshare/components/ProtectedRoute";

import Home from "@/fileshare/pages/Home";
import Pricing from "@/fileshare/pages/Pricing";
import Login from "@/fileshare/pages/Login";
import Signup from "@/fileshare/pages/Signup";
import Download from "@/fileshare/pages/Download";
import Dashboard from "@/fileshare/pages/Dashboard";
import FileRequests from "@/fileshare/pages/FileRequests";
import Settings from "@/fileshare/pages/Settings";
import FileRequestUpload from "@/fileshare/pages/FileRequestUpload";
import ForgotPassword from "@/fileshare/pages/ForgotPassword";
import Admin from "@/fileshare/pages/Admin";
import NotFound from "@/fileshare/pages/NotFound";

import "./fileshare.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<Signup />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="d/:slug" element={<Download />} />
        <Route path="r/:slug" element={<FileRequestUpload />} />
        <Route
          path="dashboard"
          element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
        />
        <Route
          path="file-requests"
          element={<ProtectedRoute requireBusiness><FileRequests /></ProtectedRoute>}
        />
        <Route
          path="settings"
          element={<ProtectedRoute><Settings /></ProtectedRoute>}
        />
        <Route
          path="admin"
          element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function FileShareApp() {
  useEffect(() => {
    document.documentElement.classList.add("fileshare-theme");
    return () => document.documentElement.classList.remove("fileshare-theme");
  }, []);

  return (
    <div className="fileshare-app">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Navbar />
          <AnimatedRoutes />
          <InstallPrompt />
          <Toaster position="top-right" richColors offset="calc(env(safe-area-inset-top) + 1rem)" />
        </AuthProvider>
      </QueryClientProvider>
    </div>
  );
}
